import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarBlank, House, CaretLeft, CaretRight } from '@phosphor-icons/react';

const COLORS = { booking: '#0052FF', cleaning: '#8B5CF6', maintenance: '#E11D48', inspection: '#F59E0B' };
const LABELS = { booking: 'Booking', cleaning: 'Cleaning', maintenance: 'Maintenance', inspection: 'Inspection' };

export default function CalendarPage() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/calendar').then(r => { setEvents(r.data.events); setProperties(r.data.properties); }).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prev = () => setMonth(new Date(year, mo - 1, 1));
  const next = () => setMonth(new Date(year, mo + 1, 1));

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      if (filter !== 'all' && e.type !== filter) return false;
      return dateStr >= e.start && dateStr <= e.end;
    });
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  if (loading) return <p className="text-sm text-zinc-500">Loading...</p>;

  return (
    <div data-testid="calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Calendar</h1>
          <p className="text-sm text-zinc-500 mt-1">Bookings, cleaning, maintenance & inspections</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'booking', 'cleaning', 'maintenance', 'inspection'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-bold rounded-sm transition-colors ${filter === f ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`} data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <button onClick={prev} className="p-2 hover:bg-zinc-100 rounded-sm" data-testid="cal-prev"><CaretLeft size={16} /></button>
          <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>{monthName}</h2>
          <button onClick={next} className="p-2 hover:bg-zinc-100 rounded-sm" data-testid="cal-next"><CaretRight size={16} /></button>
        </div>

        <div className="grid grid-cols-7 border-b border-zinc-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="px-2 py-2 text-xs font-bold text-zinc-500 text-center uppercase tracking-wide">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const dayEvents = day ? getEventsForDay(day) : [];
            const today = new Date();
            const isToday = day && year === today.getFullYear() && mo === today.getMonth() && day === today.getDate();
            return (
              <div key={i} className={`min-h-[90px] border-b border-r border-zinc-100 p-1 ${!day ? 'bg-zinc-50' : ''}`} data-testid={day ? `cal-day-${day}` : undefined}>
                {day && (
                  <>
                    <div className={`text-xs font-bold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-[#0052FF] text-white' : 'text-zinc-700'}`}>{day}</div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((e, j) => (
                        <div key={j} className="text-[10px] px-1 py-0.5 rounded-sm truncate font-medium" style={{ backgroundColor: e.color + '15', color: e.color, borderLeft: `2px solid ${e.color}` }}>
                          {e.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[10px] text-zinc-400 px-1">+{dayEvents.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex gap-6">
        {Object.entries(LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-2 text-xs text-zinc-600">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[key] }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}
